import React, { useEffect, useState, useContext } from "react";
import moment from "moment";
import {
  Typography,
  Paper,
  Card,
  CardContent,
  Chip,
  Badge,
  Button,
  useMediaQuery,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  Checkbox,
  FormControlLabel,
  FormGroup,
  IconButton,
  Box
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import MaterialTable from "@material-table/core";
import { ExportCsv, ExportPdf } from "@material-table/exporters";
import {
  ChevronRight,
  FilterList as FilterListIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Layers as LayersIcon,
  DoorFront as DoorFrontIcon,
  Payments as PaymentsIcon,
  ShoppingCart as ShoppingCartIcon,
  Close as CloseIcon,
  InfoOutlined as InfoOutlinedIcon
} from "@mui/icons-material";

import * as UsersApi from "../../../api/users";
import { EventsState, EventsTableProps } from "./types";
import { isOk } from "../../../result";
import Alert from "@material-ui/lab/Alert";
import { AlertTitle } from "@material-ui/lab";
import { splitCamelCase } from "../../../utils";
import { UserEventType } from "../../../constants";
import { prepareExportData } from "../../../utils";
import CartActions from "./CartAction";
import UserPaymentEvent from "./UserPaymentEvent";
import { UserContext } from "../../../UserProvider";
import { LifvsUserEvent } from "../../../models/User";
import { useI18n } from "../../../hooks/useI18n";

const EventsCollection = ({ userId, screenSize }: EventsTableProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const displaySize = screenSize || (isMobile ? "mobile" : "desktop");
  const rowsPerPage = displaySize === "desktop" ? 10 : 5;
  const { t } = useI18n();

  const user = useContext(UserContext);
  const primaryColor =
    user?.tenant?.configuration?.primaryColor || theme.palette.primary.main;

  const [state, setState] = useState<EventsState>({
    type: "LOADING",
    userId,
    events: [] as ReadonlyArray<LifvsUserEvent>
  });

  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [filterOpenNoPay, setFilterOpenNoPay] = useState(false);
  const [filterAnyFailedPayment, setFilterAnyFailedPayment] = useState(false);
  const [filterEmptiedCart, setFilterEmptiedCart] = useState(false);

  const useCustomStyles = () => ({
    root: {
      padding: displaySize === "mobile" ? theme.spacing(1) : theme.spacing(3),
      backgroundColor: displaySize === "mobile" ? "transparent" : "#f5f7fa",
      minHeight: displaySize === "mobile" ? "auto" : "100vh"
    },
    card: {
      marginBottom: theme.spacing(3),
      borderRadius: theme.spacing(1),
      boxShadow:
        displaySize === "mobile" ? "none" : "0 2px 10px rgba(0,0,0,0.05)",
      border: "1px solid #e0e0e0"
    },
    tableContainer: {
      "& .MuiPaper-root": {
        boxShadow: "none",
        border: "1px solid #e0e0e0",
        borderRadius: theme.spacing(1)
      }
    },
    headerContainer: {
      marginBottom: theme.spacing(3),
      display: "flex",
      flexDirection: isMobile ? "column" : "row",
      alignItems: isMobile ? "flex-start" : "center",
      justifyContent: "space-between"
    },
    eventDetailTableContainer: {
      backgroundColor: "#fafafa",
      borderRadius: "0 0 8px 8px",
      borderTop: `1px solid ${theme.palette.divider}`
    },
    headerIcon: {
      color: primaryColor,
      marginRight: theme.spacing(1)
    },
    filterBadge: {
      "& .MuiBadge-badge": {
        backgroundColor: primaryColor,
        color: "white"
      }
    },
    filterChip: {
      margin: theme.spacing(0.5),
      backgroundColor: `${primaryColor}20`,
      color: primaryColor
    },
    closeButton: {
      position: "absolute",
      right: theme.spacing(1),
      top: theme.spacing(1),
      color: theme.palette.grey[500]
    },
    filterPanel: {
      padding: theme.spacing(1.5),
      backgroundColor: `${primaryColor}10`,
      borderRadius: theme.spacing(1),
      marginBottom: theme.spacing(3),
      display: "flex",
      alignItems: "center"
    }
  });

  const styles = useCustomStyles();

  const exportDataFormat = [
    { type: "datetime", index: 0 },
    { type: "status", index: 3 },
    { type: "receiptItems", index: 5 }
  ];

  const activeFiltersCount = [
    filterOpenNoPay,
    filterAnyFailedPayment,
    filterEmptiedCart
  ].filter(Boolean).length;

  const groupEvents = events => {
    const groupedEvents = [];
    const sessionGroups = {};

    events.forEach(event => {
      if (!sessionGroups[event.sessionId]) {
        sessionGroups[event.sessionId] = [];
      }
      sessionGroups[event.sessionId].push(event);
    });

    Object.values(sessionGroups).forEach(sessionEvents => {
      let groupedRow = null;

      sessionEvents.forEach(event => {
        if (
          event.event === UserEventType.SCANNER_ON_SCAN_FOUND ||
          event.event === UserEventType.CART_INCREASE ||
          event.event === UserEventType.CART_DECREASE ||
          event.event === UserEventType.SCANNER_START_SCANNING ||
          event.event === UserEventType.CART_EMPTY
        ) {
          if (!groupedRow) {
            groupedRow = {
              ...event,
              event: "GROUPED_SCAN_ACTIONS",
              dynamicData: {
                ...event.dynamicData,
                groupedActions: [event]
              }
            };
          } else {
            groupedRow.dynamicData.groupedActions?.push(event);
          }
        } else {
          if (groupedRow) {
            groupedEvents.push(groupedRow);
            groupedRow = null;
          }
          groupedEvents.push(event);
        }
      });

      if (groupedRow) {
        groupedEvents.push(groupedRow);
      }
    });

    return groupedEvents;
  };

  const renderEvent = row => {
    let eventIcon;
    let eventLabel = row.event;
    let eventColor = theme.palette.text.primary;

    if (row.event === UserEventType.OPEN_DOOR) {
      eventIcon = (
        <DoorFrontIcon style={{ opacity: !row.successful ? 0.5 : 1 }} />
      );
      eventLabel = t("common.tabs.doorAccess");
    } else if (row.event === UserEventType.PAYMENT) {
      eventIcon = (
        <PaymentsIcon
          style={{
            color: row.successful
              ? theme.palette.success.main
              : theme.palette.text.disabled
          }}
        />
      );
      eventLabel = t("common.tabs.payment");
      eventColor = row.successful
        ? theme.palette.success.main
        : theme.palette.error.main;
    } else if (row.event === "GROUPED_SCAN_ACTIONS") {
      eventIcon = <ShoppingCartIcon style={{ color: primaryColor }} />;
      eventLabel = t("common.tabs.cartUpdates");
      eventColor = primaryColor;
    }

    return (
      <Box display="flex" alignItems="center" gap={1}>
        {eventIcon}
        {!isMobile && (
          <Typography variant="body2" color={eventColor}>
            {eventLabel}
          </Typography>
        )}
        {!row.successful && (
          <Chip
            size="small"
            label={t("common.status.failed")}
            sx={{
              backgroundColor: "#fdeded",
              color: theme.palette.error.main,
              height: "20px"
            }}
          />
        )}
      </Box>
    );
  };

  const renderEventDetailPanel = rowData => {
    const hasFailureReason = Boolean(rowData.dynamicData?.failureReason);

    return {
      icon: () => {
        if (
          hasFailureReason ||
          rowData.event === UserEventType.PAYMENT ||
          rowData.event === "GROUPED_SCAN_ACTIONS"
        ) {
          return <ChevronRight fontSize="small" />;
        }
        return <ChevronRight style={{ fontSize: 0 }} />;
      },
      render: () => {
        return (
          <Box
            sx={{
              padding: theme.spacing(2),
              backgroundColor: "#fafafa",
              borderRadius: "0 0 8px 8px",
              borderTop: `1px solid ${theme.palette.divider}`
            }}
          >
            <Box width="100%">
              {rowData.dynamicData?.paymentMethod && (
                <Box mb={1}>
                  <Chip
                    label={splitCamelCase(rowData.dynamicData.paymentMethod)}
                    size="small"
                    variant="outlined"
                  />
                </Box>
              )}

              {hasFailureReason ? (
                <Box
                  sx={{
                    padding: theme.spacing(1.5),
                    backgroundColor: "#fdeded",
                    borderRadius: theme.spacing(1)
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    <WarningIcon color="error" fontSize="small" />
                    <Typography variant="body2" color="error">
                      {splitCamelCase(rowData.dynamicData.failureReason)}
                    </Typography>
                  </Box>
                </Box>
              ) : rowData.successful ? (
                rowData.event === "GROUPED_SCAN_ACTIONS" ? (
                  <Box>
                    <Box
                      justifyContent="center"
                      flexDirection="column"
                      display="flex"
                    >
                      <CartActions
                        actions={rowData.dynamicData.groupedActions}
                      />
                    </Box>
                  </Box>
                ) : (
                  <UserPaymentEvent
                    userId={rowData.userId}
                    chargeId={rowData.dynamicData.chargeId}
                  />
                )
              ) : null}
            </Box>
          </Box>
        );
      }
    };
  };

  const getUserPatterns = events => {
    const sessionPatterns = {};

    events.forEach(event => {
      const userId = event.user?.id || event.userId;
      const sessionId = event.sessionId;

      if (!userId || !sessionId) return;

      if (!sessionPatterns[sessionId]) {
        sessionPatterns[sessionId] = {};
      }

      if (!sessionPatterns[sessionId][userId]) {
        sessionPatterns[sessionId][userId] = {
          openedDoor: false,
          madePayment: false,
          failedPayments: 0,
          emptiedCart: false,
          events: []
        };
      }

      const userSession = sessionPatterns[sessionId][userId];
      userSession.events.push(event);

      if (event.event === UserEventType.OPEN_DOOR && event.successful) {
        userSession.openedDoor = true;
      }

      if (event.event === UserEventType.PAYMENT) {
        if (event.successful) {
          userSession.madePayment = true;
        } else {
          userSession.failedPayments += 1;
        }
      }

      if (event.event === UserEventType.CART_EMPTY) {
        userSession.emptiedCart = true;
      }
    });

    return sessionPatterns;
  };

  const filterEvents = events => {
    if (!filterOpenNoPay && !filterAnyFailedPayment && !filterEmptiedCart) {
      return events;
    }

    const groupedEvents = groupEvents(events);

    const sessionPatterns = getUserPatterns(events);

    return groupedEvents.filter(event => {
      const userId = event.user?.id || event.userId;
      const sessionId = event.sessionId;

      if (!userId || !sessionId) return false;

      if (
        filterAnyFailedPayment &&
        event.event === UserEventType.PAYMENT &&
        !event.successful
      ) {
        return true;
      }

      if (
        filterEmptiedCart &&
        event.event === "GROUPED_SCAN_ACTIONS" &&
        event.dynamicData?.groupedActions?.some(
          action => action.event === UserEventType.CART_EMPTY
        )
      ) {
        return true;
      }

      if (filterOpenNoPay) {
        const userSessionData = sessionPatterns[sessionId]?.[userId];

        if (
          userSessionData &&
          userSessionData.openedDoor &&
          !userSessionData.madePayment &&
          event.event === UserEventType.OPEN_DOOR &&
          event.successful
        ) {
          return true;
        }
      }

      return false;
    });
  };

  const clearAllFilters = () => {
    setFilterOpenNoPay(false);
    setFilterAnyFailedPayment(false);
    setFilterEmptiedCart(false);
  };

  const renderFilterDialog = () => (
    <Dialog
      open={filterDialogOpen}
      onClose={() => setFilterDialogOpen(false)}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: theme.spacing(1),
          overflow: "hidden"
        }
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center">
          <FilterListIcon style={{ marginRight: 8, color: primaryColor }} />
          <Typography variant="h6">
            {t("common.messages.filterEvents")}
          </Typography>
        </Box>
        <IconButton
          aria-label="close"
          sx={styles.closeButton}
          onClick={() => setFilterDialogOpen(false)}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <FormGroup>
          <FormControlLabel
            control={
              <Checkbox
                checked={filterOpenNoPay}
                onChange={() => setFilterOpenNoPay(!filterOpenNoPay)}
                name="filterOpenNoPay"
                color="primary"
                sx={{
                  color: primaryColor,
                  "&.Mui-checked": {
                    color: primaryColor
                  }
                }}
              />
            }
            label={t("common.messages.openedDoorWithoutPurchase")}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={filterAnyFailedPayment}
                onChange={() =>
                  setFilterAnyFailedPayment(!filterAnyFailedPayment)
                }
                name="filterAnyFailedPayment"
                color="primary"
                sx={{
                  color: primaryColor,
                  "&.Mui-checked": {
                    color: primaryColor
                  }
                }}
              />
            }
            label={t("common.messages.paymentFailure")}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={filterEmptiedCart}
                onChange={() => setFilterEmptiedCart(!filterEmptiedCart)}
                name="filterEmptiedCart"
                color="primary"
                sx={{
                  color: primaryColor,
                  "&.Mui-checked": {
                    color: primaryColor
                  }
                }}
              />
            }
            label={t("common.messages.abandonedCart")}
          />
        </FormGroup>
        <Box mt={2} display="flex" justifyContent="flex-end">
          <Button
            color="inherit"
            onClick={clearAllFilters}
            style={{ marginRight: 8 }}
          >
            {t("common.actions.clearFilters")}
          </Button>
          <Button
            variant="contained"
            style={{ backgroundColor: primaryColor }}
            onClick={() => setFilterDialogOpen(false)}
          >
            {t("common.actions.apply")}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );

  const renderAppliedFilters = () => {
    if (!activeFiltersCount) return null;

    return (
      <Box mb={3}>
        <Paper
          elevation={0}
          sx={{
            padding: theme.spacing(1.5),
            backgroundColor: `${primaryColor}10`,
            borderRadius: theme.spacing(1),
            display: "flex",
            alignItems: "center"
          }}
        >
          <InfoOutlinedIcon sx={{ marginRight: 1, color: primaryColor }} />
          <Typography variant="body2" sx={{ flex: 1 }}>
            {t("common.actions.filterApplied")}
          </Typography>

          <Box>
            {filterOpenNoPay && (
              <Chip
                label={t("common.messages.doorOpenedWithoutPayment")}
                size="small"
                sx={styles.filterChip}
                onDelete={() => setFilterOpenNoPay(false)}
              />
            )}

            {filterAnyFailedPayment && (
              <Chip
                label={t("common.messages.paymentFailure")}
                size="small"
                sx={styles.filterChip}
                onDelete={() => setFilterAnyFailedPayment(false)}
              />
            )}

            {filterEmptiedCart && (
              <Chip
                label={t("common.messages.abandonedCart")}
                size="small"
                sx={styles.filterChip}
                onDelete={() => setFilterEmptiedCart(false)}
              />
            )}

            <Button
              size="small"
              onClick={clearAllFilters}
              sx={{ marginLeft: 1, color: primaryColor }}
            >
              {t("common.actions.clearFilters")}
            </Button>
          </Box>
        </Paper>
      </Box>
    );
  };

  const renderFilterBar = () => (
    <Card sx={styles.card}>
      <CardContent>
        <Box
          display="flex"
          flexDirection={isMobile ? "column" : "row"}
          alignItems={isMobile ? "flex-start" : "center"}
          mb={isMobile ? 2 : 0}
        >
          <Box display="flex" alignItems="center" mb={isMobile ? 2 : 0}>
            <LayersIcon sx={{ marginRight: 1, color: primaryColor }} />
            <Typography
              variant="subtitle2"
              component="span"
              fontWeight={500}
              marginRight={2}
            >
              {t("common.messages.events")}
            </Typography>
          </Box>

          <Box ml={isMobile ? 0 : "auto"} display="flex" gap={1}>
            <Badge
              badgeContent={activeFiltersCount || 0}
              sx={styles.filterBadge}
            >
              <Button
                startIcon={<FilterListIcon />}
                onClick={() => setFilterDialogOpen(true)}
                sx={{
                  ...(activeFiltersCount
                    ? {
                        backgroundColor: primaryColor,
                        color: "white",
                        "&:hover": {
                          backgroundColor: primaryColor
                        }
                      }
                    : {
                        color: primaryColor,
                        borderColor: primaryColor,
                        "&:hover": {
                          borderColor: primaryColor,
                          backgroundColor: `${primaryColor}10`
                        }
                      })
                }}
                variant={activeFiltersCount ? "contained" : "outlined"}
                size="small"
              >
                {t("common.actions.filter")}
              </Button>
            </Badge>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  useEffect(() => {
    switch (state.type) {
      case "LOADING":
        UsersApi.listEvents(userId).then(result => {
          if (!isOk(result)) {
            return setState({
              type: "UNEXPECTED_ERROR",
              problem: result.details
            });
          }
          return setState({
            type: "LOADED",
            userId: state.userId,
            events: groupEvents(result.value)
          });
        });
    }
  }, [state.type]);

  switch (state.type) {
    case "UNEXPECTED_ERROR":
      return (
        <Alert severity="error">
          <AlertTitle>
            {state.problem.status}, {state.problem.title}
          </AlertTitle>
          {state.problem.detail} <strong>{state.problem.traceId}</strong>
        </Alert>
      );
    case "LOADING":
    case "LOADED":
      return (
        <Box sx={styles.root}>
          {renderFilterBar()}

          {renderAppliedFilters()}

          <Box sx={styles.tableContainer}>
            <MaterialTable
              title=""
              isLoading={state.type.valueOf() === "LOADING"}
              columns={[
                {
                  title: t("common.tableHeaders.date"),
                  field: "timestamp",
                  type: "datetime",
                  cellStyle: { padding: isMobile ? "8px" : "16px" },
                  render: row => (
                    <Typography variant="body2">
                      {moment(row.timestamp).format("YYYY-MM-DD HH:mm")}
                    </Typography>
                  )
                },
                {
                  title: "Store",
                  field: "store.name",
                  hidden: true
                },
                {
                  title: "StoreId",
                  field: "store.id",
                  hidden: true
                },
                {
                  title: "UserId",
                  field: "userId",
                  hidden: true,
                  searchable: true
                },
                {
                  title: t("common.tableHeaders.event"),
                  field: "event",
                  cellStyle: { padding: isMobile ? "8px" : "16px" },
                  render: row => renderEvent(row)
                },
                {
                  title: "Status",
                  field: "successful",
                  hidden: true,
                  export: true,
                  render: row => (row.successful ? "Successful" : "Failed")
                },
                {
                  title: t("common.tableHeaders.receiptNr"),
                  field: "dynamicData.receiptId",
                  hidden: true,
                  export: true,
                  searchable: true
                },
                {
                  title: "Products & Price",
                  field: "dynamicData.receiptItems",
                  hidden: true,
                  export: true
                },
                {
                  title: t("common.tableHeaders.paymentMethod"),
                  field: "dynamicData.paymentMethod",
                  hidden: true,
                  export: true
                },
                {
                  title: t("common.tableHeaders.failureReason"),
                  field: "dynamicData.failureReason",
                  hidden: true,
                  export: true
                }
              ]}
              data={state.events ? filterEvents(state.events) : []}
              components={{
                Container: props => <Paper {...props} elevation={0} />
              }}
              detailPanel={[rowData => renderEventDetailPanel(rowData)]}
              options={{
                rowStyle: row => ({
                  backgroundColor: !row.successful ? "#fff5f5" : "white",
                  borderLeft: !row.successful
                    ? `4px solid ${theme.palette.error.light}`
                    : "none",
                  cursor: "pointer"
                }),
                headerStyle: {
                  backgroundColor: "#f5f7fa",
                  fontWeight: 600,
                  padding: isMobile ? "8px" : "16px"
                },
                showTitle: false,
                paging: true,
                pageSize: rowsPerPage,
                emptyRowsWhenPaging: false,
                search: true,
                searchFieldAlignment: "left",
                searchFieldStyle: {
                  borderRadius: theme.spacing(0.5)
                },
                pageSizeOptions: [5, 10, 20, 50, 100],
                padding: "dense",
                elevation: 0,
                exportMenu: [
                  {
                    label: t("common.export.exportPdf"),
                    exportFunc: (cols, data) => {
                      const d = prepareExportData(cols, data, exportDataFormat);
                      return ExportPdf(d[0], d[1], `UserEvents`);
                    }
                  },
                  {
                    label: t("common.export.exportCsv"),
                    exportFunc: (cols, data) => {
                      const d = prepareExportData(cols, data, exportDataFormat);
                      return ExportCsv(d[0], d[1], `UserEvents`);
                    }
                  }
                ],
                exportAllData: true
              }}
            />
          </Box>

          {renderFilterDialog()}
        </Box>
      );
  }
};

export default EventsCollection;
